import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Stack, FloppyDisk, Trash } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const BUCKET_LABELS = {
  edge: "Edge",
  profile: "Profile",
  seam: "Seam",
  "cut-out": "Cut-out",
  ignore: "Ignore",
};

const toRows = (layers) =>
  Object.keys(layers || {})
    .sort()
    .map((name) => {
      const v = layers[name];
      return {
        name,
        bucket: typeof v === "object" ? v.bucket : v,
        profile: typeof v === "object" ? v.profile || "" : "",
      };
    });

const rowsToLayers = (rows) => {
  const layers = {};
  rows.forEach((r) => {
    layers[r.name] =
      (r.bucket === "edge" || r.bucket === "profile") && r.profile
        ? { bucket: r.bucket, profile: r.profile }
        : r.bucket;
  });
  return layers;
};

// View and re-assign remembered DXF layers, and save/apply reusable presets per CAD source.
export const DxfLayerDialog = ({ open, onClose, edgeProfiles = [] }) => {
  const [buckets, setBuckets] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [rows, setRows] = useState([]);
  const [presets, setPresets] = useState([]);
  const [picked, setPicked] = useState("");
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/dxf-layers");
      setBuckets(data.buckets || []);
      setProfiles(
        (data.profiles && data.profiles.length ? data.profiles : edgeProfiles) || [],
      );
      setRows(toRows(data.layers));
      setPresets(data.presets || []);
    } catch {
      toast.error("Could not load DXF layer mappings");
    }
  }, [edgeProfiles]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const setRow = (name, patch) =>
    setRows((rs) => rs.map((r) => (r.name === name ? { ...r, ...patch } : r)));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/dxf-layers", {
        layers: rowsToLayers(rows),
      });
      setRows(toRows(data.layers));
      toast.success("Layer mappings saved — used on the next DXF import");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save layer mappings");
    } finally {
      setBusy(false);
    }
  };

  const applyPreset = async () => {
    const p = presets.find((x) => x.name === picked);
    if (!p) return;
    setBusy(true);
    try {
      const { data } = await api.put("/settings/dxf-layers", {
        layers: p.layers || {},
      });
      setRows(toRows(data.layers));
      toast.success(`Applied "${p.name}" — used on the next DXF import`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply that preset");
    } finally {
      setBusy(false);
    }
  };

  const saveAsPreset = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error("Name the preset first (e.g. Templator)");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/settings/dxf-layer-presets", {
        name,
        layers: rowsToLayers(rows),
      });
      setPresets(data.presets || []);
      setPicked(name);
      setNewName("");
      toast.success(`Saved preset "${name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that preset");
    } finally {
      setBusy(false);
    }
  };

  const deletePreset = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      const { data } = await api.delete(
        `/settings/dxf-layer-presets/${encodeURIComponent(picked)}`,
      );
      setPresets(data.presets || []);
      setPicked("");
      toast.success("Preset deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete that preset");
    } finally {
      setBusy(false);
    }
  };

  const selCls =
    "rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-xs text-zinc-100 focus:border-indigo-400 focus:outline-none";
  const miniBtn =
    "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors disabled:opacity-40";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="dxf-layers-dialog"
        className="max-w-xl border-zinc-800 bg-zinc-950 text-zinc-100"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-zinc-100">
            <Stack size={18} weight="bold" className="text-indigo-300" />
            DXF layer mapping
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Choose how each CAD layer imports. Mappings are remembered by layer
            name; save a whole map as a preset per CAD source.
          </DialogDescription>
        </DialogHeader>

        <div
          data-testid="dxf-presets-bar"
          className="flex flex-wrap items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2"
        >
          <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Preset
          </span>
          <select
            data-testid="dxf-preset-select"
            value={picked}
            onChange={(e) => setPicked(e.target.value)}
            className={`${selCls} min-w-[9rem]`}
          >
            <option value="">Choose a preset…</option>
            {presets.map((p) => (
              <option key={p.name} value={p.name}>
                {p.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            data-testid="dxf-preset-apply"
            disabled={busy || !picked}
            onClick={applyPreset}
            className={`${miniBtn} border-indigo-400/50 bg-indigo-500/15 text-indigo-200 hover:bg-indigo-500/25`}
          >
            Apply
          </button>
          <button
            type="button"
            data-testid="dxf-preset-delete"
            disabled={busy || !picked}
            onClick={deletePreset}
            className={`${miniBtn} border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-rose-300`}
          >
            <Trash size={12} weight="bold" />
          </button>
          <span className="mx-1 h-5 w-px bg-zinc-800" />
          <input
            data-testid="dxf-preset-name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New preset name"
            className={`${selCls} w-32`}
          />
          <button
            type="button"
            data-testid="dxf-preset-save"
            disabled={busy || rows.length === 0}
            onClick={saveAsPreset}
            className={`${miniBtn} border-zinc-700 text-zinc-200 hover:bg-zinc-800`}
          >
            <FloppyDisk size={12} weight="bold" />
            Save as
          </button>
        </div>

        {rows.length === 0 ? (
          <div
            data-testid="dxf-layers-empty"
            className="rounded-lg border border-dashed border-zinc-800 py-8 text-center text-sm text-zinc-500"
          >
            No layers remembered yet. Import a DXF / XDF template first, or apply a
            preset above.
          </div>
        ) : (
          <div className="max-h-[42vh] space-y-2 overflow-auto pr-1">
            {rows.map((r) => (
              <div
                key={r.name}
                data-testid={`dxf-layer-row-${r.name}`}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-2"
              >
                <span className="min-w-0 flex-1 truncate font-mono text-xs text-zinc-200">
                  {r.name}
                </span>
                <select
                  data-testid={`dxf-layer-bucket-${r.name}`}
                  value={r.bucket}
                  onChange={(e) => setRow(r.name, { bucket: e.target.value })}
                  className={selCls}
                >
                  {buckets.map((b) => (
                    <option key={b} value={b}>
                      {BUCKET_LABELS[b] || b}
                    </option>
                  ))}
                </select>
                {(r.bucket === "edge" || r.bucket === "profile") && (
                  <select
                    data-testid={`dxf-layer-profile-${r.name}`}
                    value={r.profile}
                    onChange={(e) => setRow(r.name, { profile: e.target.value })}
                    className={selCls}
                  >
                    <option value="">Default (Eased)</option>
                    {profiles.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            data-testid="dxf-layers-cancel"
            onClick={onClose}
            className="rounded-md border border-zinc-700 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors hover:bg-zinc-800"
          >
            Close
          </button>
          <button
            type="button"
            data-testid="dxf-layers-save"
            disabled={busy || rows.length === 0}
            onClick={save}
            className="rounded-md border border-indigo-400/50 bg-indigo-500/15 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-indigo-200 transition-colors hover:bg-indigo-500/25 disabled:opacity-40"
          >
            {busy ? "Saving…" : "Save mappings"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
